import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form } from 'antd';

import { type OnboardingStep } from '@/entities/onboarding';

import { logError } from '@/shared/lib/logging';
import { useAppDispatch } from '@/shared/lib/store';
import { InputText, PhoneInput } from '@/shared/ui/input';

import {
    usePhone,
    useUsername,
} from '../../../model/selectors/onboarding.selectors';
import { setUsernameAndPhone } from '../../../model/slices/onboarding.slice';
import { OnboardingStepWrapper } from '../../onboarding-step-wrapper/onboarding-step-wrapper';

import cls from './username-and-phone-onboarding-step.module.css';

interface UsernameAndPhoneOnboardingStepProps {
    step: OnboardingStep;
    onNext?: (() => void) | undefined;
    isLastStep?: boolean | undefined;
    isProcessing?: boolean | undefined;
}

export const UsernameAndPhoneOnboardingStep = memo(
    (props: UsernameAndPhoneOnboardingStepProps) => {
        const { step, onNext, isLastStep, isProcessing } = props;
        const { t } = useTranslation('onboarding');

        const [form] = Form.useForm();
        const [isLoading, setIsLoading] = useState(false);
        const dispatch = useAppDispatch();

        const existingUsername = useUsername();
        const existingPhone = usePhone();

        // Initialize form with existing data
        useEffect(() => {
            form.setFieldsValue({
                username: existingUsername,
                phone: existingPhone,
            });
        }, [form, existingUsername, existingPhone]);

        const handleSubmit = async () => {
            try {
                setIsLoading(true);

                // Validate form fields before proceeding
                const values = await form.validateFields();

                // Save data to Redux store
                dispatch(
                    setUsernameAndPhone({
                        username: values.username || '',
                        phone: values.phone || '',
                    }),
                );

                onNext?.();
                setIsLoading(false);
            } catch (error) {
                logError('Form validation failed:', error);
                setIsLoading(false);
            }
        };

        return (
            <OnboardingStepWrapper
                className={cls.maxWidth}
                isLastStep={isLastStep}
                isProcessing={isProcessing || isLoading}
                onNext={handleSubmit}
                step={step}
            >
                <Form
                    form={form}
                    layout="vertical"
                    style={{ width: '100%' }}
                >
                    <Form.Item
                        label={t('usernameAndPhone.username.label')}
                        name="username"
                        rules={[
                            {
                                required: true,
                                message: t(
                                    'usernameAndPhone.username.required',
                                ),
                            },
                        ]}
                    >
                        <InputText
                            isBlock
                            placeholder={t(
                                'usernameAndPhone.username.placeholder',
                            )}
                        />
                    </Form.Item>

                    <Form.Item
                        label={t('usernameAndPhone.phone.label')}
                        name="phone"
                        rules={[
                            {
                                required: true,
                                message: t('usernameAndPhone.phone.required'),
                            },
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve(); // laisser le required gérer le cas vide

                                    // Validate full phone number format (country code + number)
                                    const indicativeRegex = /^\+[1-9]\d*$/;
                                    const lengthRegex = /^\+\d{10,15}$/;

                                    if (!indicativeRegex.test(value)) {
                                        return Promise.reject(
                                            new Error(
                                                t(
                                                    'usernameAndPhone.phone.invalid',
                                                ),
                                            ),
                                        );
                                    }

                                    if (!lengthRegex.test(value)) {
                                        return Promise.reject(
                                            new Error(
                                                t(
                                                    'usernameAndPhone.phone.tooShort',
                                                ),
                                            ),
                                        );
                                    }

                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <PhoneInput
                            placeholder={t(
                                'usernameAndPhone.phone.placeholder',
                            )}
                        />
                    </Form.Item>
                </Form>
            </OnboardingStepWrapper>
        );
    },
);

UsernameAndPhoneOnboardingStep.displayName = 'UsernameAndPhoneOnboardingStep';
